/**
 * 판정 기록 상세 — 결과 카드·원문 3블록·삭제(6120)/원문 삭제(purge) 상태.
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 라우트 요소는 logId를 key로 한 래퍼(브라우저 뒤로가기로 다른 상세에 와도 앞 상태가 남지 않게, D-19).
 * 당시 룰 본문(RuleVersionBlock)·이전/다음·직접 진입 기간 고정은 단계 12·13에서 더한다.
 */
import React, { useEffect, useReducer, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import Tag from "components/atoms/Tag";
import ResultView from "components/organisms/DoseReviewResultView";
import RawView from "components/organisms/DoseReviewResultView/RawView";
import { ToastManager } from "components/organisms/CommonUI";
import { DoseApiError } from "services/api/doseApiError";
import {
  useDoseReviewLogDetail,
  useDoseReviewLogNeighbors,
  useDoseRuleVersionDetails,
  doseReviewLogKeys,
} from "hooks/doseReviewLogs/useDoseReviewLogs";
import {
  useDoseReviewLabelMutations,
  applyLabelToDetailCaches,
  invalidateAfterLabelChange,
} from "hooks/doseReviewLogs/useDoseReviewLabelMutations";
import {
  parseFilters,
  filtersToSearch,
  periodToApiRange,
  apiRangeToPeriod,
  toApiFilter,
  toCardRequest,
  rowIdsToFetch,
  backToListOffset,
  formatDosage,
  DELETED_LOG_TEXT,
  PURGED_DETAIL_TEXT,
  LIST_ERROR_TEXT,
  RETRY_LABEL,
} from "utils/doseReviewLogs";
import {
  labelLineReducer,
  editingCount,
  labelStateText,
  toPutBody,
  isSelfConflict,
  conflictLabel,
  RULE_NOTE_APPENDED_TOAST,
  SAVE_FAILED_TOAST,
  CONFLICT_TOAST,
  ALREADY_SAVED_TOAST,
  UNLABEL_FAILED_TOAST,
} from "utils/doseReviewLabels";
import { useAdminNavGuard } from "components/templates/AdminLayout/NavGuardContext";
import { cardBadge, cardTitleName } from "components/organisms/DoseReviewResultView/cardModel";
import { severityLabel, severityTone } from "components/organisms/DoseReviewResultView/verdict";
import type { DoseReviewData, DoseReviewItem } from "api/doseReviewService";
import type { DoseLabel, DoseReviewLogDetailItem } from "services/api/doseReviewLogs/types";
import type { RootState } from "store";
import { PageWrap, PageTitle } from "./filterStyle";
import { Section, SectionTitle, ListName, StateBox, Nav, NavButtons } from "./detailStyle";
import RuleVersionBlock, { type VersionDetailState } from "./RuleVersionBlock";
import LabelControl from "./LabelControl";
import UnsavedBanner from "./UnsavedBanner";
import PrescriptionSummary from "./PrescriptionSummary";
import PrescriptionTable, { type PrescriptionRow } from "./PrescriptionTable";
import { LayoutButtons } from "./layoutStyle";

const openRuleInNewTab = (ruleId: string) =>
  window.open("/admin/dose-rules/" + encodeURIComponent(ruleId), "_blank", "noopener");

const DoseReviewLogDetail: React.FC<{ logId: number }> = ({ logId }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showRaw, setShowRaw] = useState(false);

  // 정오 표기 — 줄 상태(결정 A1)·뮤테이션(결정 G1)·로그인 사용자 id(T18·D-9).
  const userId = useSelector((s: RootState) => s.user.user?.id ?? null);
  const [lines, dispatch] = useReducer(labelLineReducer, {});
  const { putLabel, deleteLabel } = useDoseReviewLabelMutations();

  // 저장 안 한 초안 이동 배너(T12~T15·D-19) — 보류할 이동 대상 경로.
  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const editing = editingCount(lines);
  // 관리자 사이드 메뉴 가드 등록(언마운트 해제는 훅이 처리) — 최신 lines는 useAdminNavGuard의 ref로 본다.
  useAdminNavGuard((path) => {
    if (editingCount(lines) > 0) {
      setPendingNav(path);
      return false;
    }
    return true;
  });
  // ③ 배너 표시 중 작성 중 줄이 0이 되면(저장·취소) 배너를 닫는다(이동 없음).
  useEffect(() => {
    if (pendingNav !== null && editing === 0) setPendingNav(null);
  }, [pendingNav, editing]);

  // 약별 상세 접이식(상세 화면 개편) — 기본 전부 접힘. CSS 접힘이라 초안(reducer)은 접어도 유지된다.
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  useEffect(() => {
    if (scrollTo == null) return;
    const el = document.querySelector(`[data-dose-item="${scrollTo}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollTo(null);
  }, [scrollTo]);

  const filters = parseFilters(searchParams);
  const hasPeriod = !!searchParams.get("start") && !!searchParams.get("end");
  const range = hasPeriod ? periodToApiRange(filters.start, filters.end) : undefined;

  const detail = useDoseReviewLogDetail(logId, range);

  // 당시 룰 본문 — 훅 순서 유지를 위해 early return 전에 호출(data 없으면 rowIds 빈 배열).
  const ruleVersions = detail.data?.ruleVersions ?? [];
  const rowIds = rowIdsToFetch(ruleVersions);
  const versionQueries = useDoseRuleVersionDetails(rowIds);
  const versionDetails = new Map<number, VersionDetailState>();
  rowIds.forEach((rowId, i) => {
    const q = versionQueries[i] as { data?: unknown; isLoading?: boolean; isError?: boolean } | undefined;
    versionDetails.set(rowId, {
      data: q?.data as VersionDetailState["data"],
      isLoading: !!q?.isLoading,
      isError: !!q?.isError,
    });
  });

  // 이전/다음·순번 — 기간이 URL에 있을 때만(from·to 필수). 직접 진입은 기간 고정 뒤에 켜진다.
  const neighbors = useDoseReviewLogNeighbors(logId, toApiFilter(filters), hasPeriod);

  // 직접 진입 기간 고정(결정 C1) — 첫 응답 sameCaseRange로 새 키를 시드하고 URL start·end를 replace.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (detail.data && !hasPeriod) {
      const period = apiRangeToPeriod(detail.data.sameCaseRange);
      const seededRange = periodToApiRange(period.start, period.end);
      queryClient.setQueryData(doseReviewLogKeys.detail(logId, seededRange), detail.data);
      navigate(
        "/admin/dose-review-logs/" + logId + "?" + filtersToSearch({ ...filters, ...period }),
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data, hasPeriod]);

  // SPA 안 이동(상세 컨트롤) — 작성 중 줄이 있으면 보류하고 배너(T12). 없으면 바로 이동(T15).
  const guardedNavigate = (to: string) => {
    if (editingCount(lines) > 0) setPendingNav(to);
    else navigate(to);
  };

  const goToList = (offset: number) =>
    guardedNavigate("/admin/dose-review-logs?" + filtersToSearch({ ...filters, offset }));

  const goToNeighbor = (id: number | null | undefined) => {
    if (id == null) return;
    guardedNavigate("/admin/dose-review-logs/" + id + "?" + filtersToSearch(filters));
  };

  // [버리고 이동](T13) — 모든 초안 폐기 후 누른 대상으로(사이드 메뉴 가드를 다시 타지 않게 navigate 직접).
  const discardAndGo = () => {
    const to = pendingNav;
    dispatch({ type: "discardAll" });
    setPendingNav(null);
    if (to) navigate(to);
  };
  // [머물기](T14) — 배너만 닫고 이동하지 않는다(초안 유지).
  const stay = () => setPendingNav(null);

  const neighborsReady = hasPeriod && !neighbors.isLoading && !neighbors.isError;
  const prevId = neighborsReady ? neighbors.data?.prevId ?? null : null;
  const nextId = neighborsReady ? neighbors.data?.nextId ?? null : null;
  const backOffset = backToListOffset(neighborsReady ? neighbors.data?.position ?? null : null, 50);

  if (detail.isLoading) {
    return (
      <PageWrap>
        <StateBox data-testid="dose-detail-loading">
          <Spinner />
        </StateBox>
      </PageWrap>
    );
  }

  if (detail.isError) {
    const err = detail.error;
    const isDeleted = err instanceof DoseApiError && err.code === 6120;
    return (
      <PageWrap>
        <StateBox data-testid={isDeleted ? "dose-detail-deleted" : "dose-detail-error"}>
          <div>{isDeleted ? DELETED_LOG_TEXT : LIST_ERROR_TEXT}</div>
          {isDeleted ? (
            <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={() => goToList(0)} data-testid="dose-detail-to-list">
              목록으로
            </Button>
          ) : (
            <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={() => detail.refetch()}>
              {RETRY_LABEL}
            </Button>
          )}
        </StateBox>
      </PageWrap>
    );
  }

  const data = detail.data;
  if (!data) return null;
  const log = data.log;
  const purged = log.result == null;

  // 보유 표기 = override(T7·T10) ?? 상세 items[].label(결정 A1 — 성공 시 캐시가 단일 진실).
  const heldOf = (di: DoseReviewLogDetailItem): DoseLabel | null => {
    const o = lines[di.index]?.override;
    return o ? o.label : di.label;
  };
  const sameCaseIndexesOf = (caseId: string): number[] =>
    data.items.filter((it) => it.caseId === caseId).map((it) => it.index);

  // 성공(T5·T9·T18) — 캐시의 같은 caseId 줄 교체 + 그 줄 보기·초안 폐기(T17) + neighbors·목록·요약 무효화.
  const settle = (di: DoseReviewLogDetailItem, caseId: string, label: DoseLabel | null) => {
    applyLabelToDetailCaches(queryClient, caseId, label);
    dispatch({ type: "settled", index: di.index, sameCaseIndexes: sameCaseIndexesOf(caseId) });
    invalidateAfterLabelChange(queryClient);
  };

  const save = async (di: DoseReviewLogDetailItem) => {
    const line = lines[di.index];
    if (!line?.draft) return;
    const sent = line.draft;
    const held = heldOf(di);
    dispatch({ type: "saveStart", index: di.index }); // T4
    try {
      const r = await putLabel.mutateAsync({
        logId,
        index: di.index,
        body: toPutBody(sent, held?.revision ?? 0),
      });
      settle(di, r.caseId, r.label); // T5(+T17)
      if (r.ruleNoteAppended) ToastManager.show({ content: RULE_NOTE_APPENDED_TOAST });
    } catch (e) {
      if (e instanceof DoseApiError && e.code === 6122) {
        const label = conflictLabel(e.data);
        const caseId = (e.data as { caseId?: string } | null)?.caseId ?? di.caseId ?? "";
        if (isSelfConflict(sent, label, userId)) {
          settle(di, caseId, label); // T18 — "이미 저장됨"(T5와 같다)
          ToastManager.show({ content: ALREADY_SAVED_TOAST });
        } else {
          dispatch({ type: "saveConflict", index: di.index, label }); // T7 — 초안 보존·override
          ToastManager.show({ content: CONFLICT_TOAST });
        }
      } else {
        dispatch({ type: "saveFail", index: di.index }); // T6 — 초안 보존
        ToastManager.show({ content: SAVE_FAILED_TOAST });
      }
    }
  };

  const unlabel = async (di: DoseReviewLogDetailItem) => {
    const held = heldOf(di);
    dispatch({ type: "unlabelStart", index: di.index }); // T8
    try {
      const r = await deleteLabel.mutateAsync({
        logId,
        index: di.index,
        expectedRevision: held?.revision ?? 0,
      });
      settle(di, r.caseId, null); // T9
    } catch (e) {
      if (e instanceof DoseApiError && e.code === 6122) {
        dispatch({ type: "unlabelConflict", index: di.index, label: conflictLabel(e.data) }); // T10
        ToastManager.show({ content: CONFLICT_TOAST });
      } else {
        dispatch({ type: "unlabelFail", index: di.index }); // T11
        ToastManager.show({ content: UNLABEL_FAILED_TOAST });
      }
    }
  };

  const cardRequest = toCardRequest(log.request);
  const renderItemTitleExtra = (item: DoseReviewItem) => {
    const listName = log.products?.find((p) => p.index === item.index)?.name;
    const cardName = cardRequest.prescriptions?.[item.index]?.medicationName;
    if (listName && cardName && listName !== cardName) {
      return <ListName data-testid="dose-detail-list-name">목록: {listName}</ListName>;
    }
    return null;
  };

  // 접이식 제어(상세 화면 개편) — 문제 줄 = di.isAlert(위험·1회 과량·경고, 강등 전 축).
  const resultItems = (log.result?.items ?? []) as DoseReviewItem[];
  const allIndexes = resultItems.map((it) => it.index);
  const problemIndexes = data.items.filter((di) => di.isAlert).map((di) => di.index);
  const toggleItem = (index: number) =>
    setExpandedItems((prev) => {
      const n = new Set(prev);
      if (n.has(index)) n.delete(index);
      else n.add(index);
      return n;
    });
  const expandAll = () => setExpandedItems(new Set(allIndexes));
  const expandProblems = () => setExpandedItems(new Set(problemIndexes));
  const collapseAll = () => setExpandedItems(new Set());
  const rowClick = (index: number) => {
    setExpandedItems((prev) => new Set(prev).add(index));
    setScrollTo(index);
  };

  // 처방 목록 표 행(줄 전용 등급은 cardBadge/severityLabel 재사용·정오 상태는 held/mode).
  const prescriptionRows: PrescriptionRow[] = resultItems.map((it) => {
    const di = data.items.find((d) => d.index === it.index);
    const badge = cardBadge(it);
    const p = cardRequest.prescriptions[it.index] ?? {};
    return {
      index: it.index,
      name: cardTitleName(cardRequest, it.index) || `약품 ${it.index + 1}`,
      dosage: formatDosage(p.dosagePerAdministration, p.timesPerDay, p.daysOfAdministration),
      grade: badge
        ? {
            label: severityLabel(badge.severity, badge.status),
            tone: severityTone(badge.severity, badge.status),
            downgraded: badge.downgraded,
          }
        : null,
      isProblem: !!di?.isAlert,
      labelState: labelStateText(di ? heldOf(di) : null, di ? lines[di.index]?.mode : undefined),
    };
  });

  // 접힌/펼친 카드 헤더의 정오 상태·"작성 중"(renderItemHeaderExtra 슬롯).
  const renderItemHeaderExtra = (item: DoseReviewItem) => {
    const di = data.items.find((d) => d.index === item.index);
    const mode = di ? lines[di.index]?.mode : undefined;
    return (
      <span data-testid="dose-card-label-state">
        <Tag size="Small" variant="Tinted" color={mode === "editing" ? "Blue" : "Gray"}>
          {labelStateText(di ? heldOf(di) : null, mode)}
        </Tag>
      </span>
    );
  };

  return (
    <PageWrap data-testid="dose-detail-page">
      {pendingNav !== null && (
        <UnsavedBanner count={editing} onDiscard={discardAndGo} onStay={stay} />
      )}
      <Nav>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Ghost"
          color="Secondary"
          onClick={() => goToList(backOffset)}
          data-testid="dose-detail-back"
        >
          ← 목록
        </Button>
        <NavButtons>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={prevId == null}
            onClick={() => goToNeighbor(prevId)}
            data-testid="dose-detail-prev"
          >
            이전
          </Button>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={nextId == null}
            onClick={() => goToNeighbor(nextId)}
            data-testid="dose-detail-next"
          >
            다음
          </Button>
        </NavButtons>
      </Nav>

      <PageTitle>판정 기록 #{log.id}</PageTitle>

      <PrescriptionSummary
        requestedAt={log.requestedAt}
        source={log.source}
        log={log}
        patient={
          log.result
            ? { ageMonths: log.result.patient?.ageMonths ?? null, weightKg: log.result.patient?.weightKg ?? null }
            : null
        }
        prescriptionFindings={log.result?.prescriptionFindings ?? []}
      />

      {purged ? (
        <StateBox data-testid="dose-detail-purged">
          <div>{PURGED_DETAIL_TEXT}</div>
        </StateBox>
      ) : (
        <>
          <Section>
            <SectionTitle>처방 목록</SectionTitle>
            <PrescriptionTable rows={prescriptionRows} onRowClick={rowClick} />
          </Section>

          <Section>
            <SectionTitle>약품별 상세</SectionTitle>
            <LayoutButtons>
              <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={expandAll} data-testid="dose-expand-all">
                모두 펼치기
              </Button>
              <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={expandProblems} data-testid="dose-expand-problems">
                문제 펼치기
              </Button>
              <Button buttonType="button" type="Text" size="Small" variant="Outlined" color="Secondary" onClick={collapseAll} data-testid="dose-collapse-all">
                모두 접기
              </Button>
            </LayoutButtons>
            <ResultView
              data={log.result as DoseReviewData}
              request={cardRequest}
              onOpenRule={openRuleInNewTab}
              renderItemTitleExtra={renderItemTitleExtra}
              collapsible
              expandedItems={expandedItems}
              onToggleItem={toggleItem}
              renderItemHeaderExtra={renderItemHeaderExtra}
              suppressPrescriptionFindings
              renderItemFooter={(item) => {
                // 상세 items[]에서 그 index를 찾아 정오 컨트롤(⑬ — 없으면 안 그린다). 순서: 컨트롤 → 당시 룰(①).
                const di = data.items.find((d) => d.index === item.index);
                return (
                  <>
                    {di ? (
                      <LabelControl
                        item={di}
                        held={heldOf(di)}
                        line={lines[di.index] ?? { mode: "view", draft: null }}
                        sameCaseRange={data.sameCaseRange}
                        onVerdict={(verdict) =>
                          dispatch({ type: "verdict", index: di.index, verdict, held: heldOf(di) })
                        }
                        onSeverity={(value) => dispatch({ type: "severity", index: di.index, value })}
                        onNote={(value) => dispatch({ type: "note", index: di.index, value })}
                        onSave={() => save(di)}
                        onDraftCancel={() => dispatch({ type: "draftCancel", index: di.index })}
                        onUnlabel={() => unlabel(di)}
                      />
                    ) : null}
                    <RuleVersionBlock
                      item={item}
                      ruleVersions={data.ruleVersions}
                      rules={log.rules}
                      versionDetails={versionDetails}
                    />
                  </>
                );
              }}
              onShowRaw={() => setShowRaw(true)}
            />
          </Section>

          <Section>
            <Button
              buttonType="button"
              type="Text"
              size="Small"
              variant="Ghost"
              color="Secondary"
              onClick={() => setShowRaw((v) => !v)}
              data-testid="dose-detail-raw-toggle"
            >
              {showRaw ? "원문 접기" : "원문 보기"}
            </Button>
            {showRaw && (
              <RawView
                blocks={[
                  { key: "request", title: "요청", value: log.request, testId: "dose-raw-request", copyTestId: "dose-raw-copy-request" },
                  { key: "rules", title: "룰", value: log.rules, testId: "dose-raw-rules", copyTestId: "dose-raw-copy-rules" },
                  { key: "result", title: "결과", value: log.result, testId: "dose-raw-result", copyTestId: "dose-raw-copy-result" },
                ]}
              />
            )}
          </Section>
        </>
      )}
    </PageWrap>
  );
};

/** 라우트 요소 래퍼 — logId를 key로 새 인스턴스를 만든다(D-19). */
const DoseReviewLogDetailRoute: React.FC = () => {
  const { logId } = useParams<{ logId: string }>();
  return <DoseReviewLogDetail key={logId} logId={Number(logId)} />;
};

export default DoseReviewLogDetailRoute;
export { DoseReviewLogDetail };
